import React, { Component, useCallback, useEffect } from 'react';
import noop from 'lodash/noop';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { RealtimeEvent, RealtimeEventType } from './common/types/events.tsx';
import type { EventPayload } from './common/types/payloads.tsx';
import { RealtimeClient } from './services/realtime-client/index.tsx';
import { RealtimeProvider } from './services/realtime-provider/index.tsx';
import { RealtimeSubscriptions } from './services/realtime-subscriptions/RealtimeSubscriptions.tsx';

import { SOURCE_FPS, SOURCE_SUBSCRIPTIONS } from './common/constants/sources.tsx';
import { analyticEvents, analyticsComponent } from './common/constants/analyticEvents.tsx';

const LOG_LOCATION = 'jira-realtime';

export type Props = {
	appId: string;
	channels: string[];
	events: string[];
	onJoin: (channels: string[]) => void;
	onReceive: (event: RealtimeEvent) => void;
	onLeave: (channels: string[]) => void;
};

// eslint-disable-next-line jira/react/no-class-components
class RealtimeOld extends Component<Props> {
	static defaultProps = {
		onJoin: noop,
		onReceive: noop,
		onLeave: noop,
	};

	onEventReceive = (eventType: RealtimeEventType, payload: EventPayload) => {
		const event: RealtimeEvent = {
			type: eventType,
			payload,
		};

		this.props.onReceive(event);
	};

	logUnsupportedChannels = (channels: string[], appId: string) => {
		const unsupportedChannels = channels.filter((channel) => !JiraProjectAri.check(channel));

		if (unsupportedChannels.length > 0) {
			log.safeWarnWithoutCustomerData(LOG_LOCATION, 'Unsupported channels', {
				source: fg('jirt-use-graphql-subscriptions') ? SOURCE_SUBSCRIPTIONS : SOURCE_FPS,
				appId,
				unsupportedChannels: unsupportedChannels?.join(', '),
			});
		}
	};

	render() {
		const { appId, events, onJoin, onLeave, channels, onReceive } = this.props;

		if (fg('jirt-log-unsupported-channels')) {
			this.logUnsupportedChannels(channels, appId);
		}

		if (fg('jirt-use-graphql-subscriptions')) {
			return (
				<RealtimeSubscriptions
					appId={appId}
					onJoin={onJoin}
					onLeave={onLeave}
					channels={channels}
					events={events}
					onReceive={onReceive}
				/>
			);
		}

		return (
			<RealtimeProvider appId={appId} onJoin={onJoin} onLeave={onLeave} channels={channels}>
				<RealtimeClient appId={appId} events={events} onReceive={this.onEventReceive} />
			</RealtimeProvider>
		);
	}
}

function RealtimeNew({
	appId,
	channels,
	events,
	onJoin = noop,
	onReceive = noop,
	onLeave = noop,
}: Props) {
	const atlassianAccountId = useAccountId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const logUnsupportedChannels = useCallback(() => {
		const unsupportedChannels = channels.filter((channel) => !JiraProjectAri.check(channel));
		if (unsupportedChannels.length > 0) {
			log.safeWarnWithoutCustomerData(LOG_LOCATION, 'Unsupported channels', {
				source: fg('jirt-use-graphql-subscriptions') ? SOURCE_SUBSCRIPTIONS : SOURCE_FPS,
				appId,
				unsupportedChannels: unsupportedChannels?.join(', '),
			});
		}
	}, [appId, channels]);

	const onEventReceive = useCallback(
		(eventType: RealtimeEventType, payload: EventPayload, analyticsEvent: UIAnalyticsEvent) => {
			const event: RealtimeEvent = {
				type: eventType,
				payload,
			};
			fireOperationalAnalytics(
				analyticsEvent,
				`${analyticsComponent} ${analyticEvents.EVENT_RECEIVED}`,
				{
					source: SOURCE_FPS,
					eventType,
					appId,
					isTriggeredBySameUser: payload?.atlassianId === atlassianAccountId,
				},
			);
			onReceive(event);
		},
		[appId, atlassianAccountId, onReceive],
	);

	const onError = useCallback(
		(error: Error) => {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`${analyticsComponent} ${analyticEvents.FAILURE}`,
				{
					source: SOURCE_FPS,
					appId,
					channels,
					events,
					error: error.message,
				},
			);
		},
		[appId, channels, createAnalyticsEvent, events],
	);

	useEffect(() => {
		if (fg('jirt-log-unsupported-channels')) {
			logUnsupportedChannels();
		}
	}, [channels, appId, logUnsupportedChannels]);

	if (fg('jirt-use-graphql-subscriptions')) {
		return (
			<RealtimeSubscriptions
				appId={appId}
				onJoin={onJoin}
				onLeave={onLeave}
				channels={channels}
				events={events}
				onReceive={onReceive}
			/>
		);
	}

	return (
		<RealtimeProvider
			appId={appId}
			onJoin={onJoin}
			onLeave={onLeave}
			channels={channels}
			onError={onError}
		>
			<RealtimeClient appId={appId} events={events} onReceive={onEventReceive} />
		</RealtimeProvider>
	);
}

const Realtime = componentWithFG(
	'jirt-convert-class-to-functional-component',
	RealtimeNew,
	RealtimeOld,
);

export default Realtime;
